/**
 * ChannelPermissionOverrides — Allow / Deny / Inherit permission editor
 * =====================================================================
 *
 * One target at a time (@everyone → each role → each member), showing
 * all permissions with a 3-state toggle. Saves/upserts/deletes rows in
 * public.channel_permission_overrides.
 *
 * State mapping:
 *   - Inherit   → no row (or empty masks — row gets deleted)
 *   - Allow (✓) → permission in allow_mask
 *   - Deny  (✖) → permission in deny_mask
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, Check, X as XIcon, Minus, Users, User, ChevronDown, Save, RotateCcw } from 'lucide-react';
import { supabase } from '../supabase';
import { PERMISSIONS, type PermissionMeta } from '../core/permissions/permissions';

// ── types ──────────────────────────────────────────────────────────

interface ServerRole {
  id: string;
  name: string;
  color?: string;
  position: number;
  role_kind?: string;
  system_role_id?: string;
}

interface VaultMember {
  user_id: string;
  display_name?: string;
  photo_url?: string;
}

interface OverrideRow {
  id?: string;
  channel_id: string;
  role_id?: string | null;
  user_id?: string | null;
  allow_mask: string[];
  deny_mask: string[];
}

type ToggleState = 'inherit' | 'allow' | 'deny';

interface Props {
  channelId: string;
  vaultId: string;
}

// ── helpers ────────────────────────────────────────────────────────

function nextToggle(current: ToggleState): ToggleState {
  if (current === 'inherit') return 'allow';
  if (current === 'allow') return 'deny';
  return 'inherit';
}

const TOGGLE_ICON: Record<ToggleState, React.ReactNode> = {
  inherit: <Minus size={12} />,
  allow: <Check size={12} />,
  deny: <XIcon size={12} />,
};

const TOGGLE_CLASS: Record<ToggleState, string> = {
  inherit: 'bg-white/5 border-white/10 text-white/30',
  allow: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  deny: 'bg-red-500/10 border-red-500/30 text-red-400',
};

const TOGGLE_LABEL: Record<ToggleState, string> = {
  inherit: 'Inherit',
  allow: 'Allow',
  deny: 'Deny',
};

/** Group permissions by category for the UI */
const CATEGORY_ORDER: PermissionMeta['category'][] = [
  'general', 'members', 'messages', 'channels', 'voice', 'roles', 'invites', 'meta',
];
const CATEGORY_LABEL: Record<string, string> = {
  general: 'General',
  members: 'Members',
  messages: 'Messages',
  channels: 'Channels',
  voice: 'Voice',
  roles: 'Roles',
  invites: 'Invites',
  meta: 'Meta',
};

function groupByCategory(perms: PermissionMeta[]): Map<string, PermissionMeta[]> {
  const map = new Map<string, PermissionMeta[]>();
  for (const p of perms) {
    const bucket = map.get(p.category) ?? [];
    bucket.push(p);
    map.set(p.category, bucket);
  }
  return map;
}

// ── component ──────────────────────────────────────────────────────

export default function ChannelPermissionOverrides({ channelId, vaultId }: Props) {
  // Data
  const [roles, setRoles] = useState<ServerRole[]>([]);
  const [members, setMembers] = useState<VaultMember[]>([]);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // UI state
  const [selectedTarget, setSelectedTarget] = useState<string>('everyone'); // 'everyone' | role:<id> | user:<id>
  const [dirtyMask, setDirtyMask] = useState<Record<string, ToggleState>>({}); // permId → state
  const [showTargetMenu, setShowTargetMenu] = useState(false);

  // ── fetch all data ──────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: allRoles }, { data: vaultRow }, { data: overrideRows }] = await Promise.all([
      supabase.from('server_roles').select('id, name, position, vault_id, role_kind, permission_ids').eq('vault_id', vaultId).order('position'),
      // Membership is `vaults.members` (uuid[]); the `vault_members`
      // junction table this used to query does not exist in this schema, so
      // the per-user override target list was always empty.
      supabase.from('vaults').select('members').eq('id', vaultId).maybeSingle(),
      supabase.from('channel_permission_overrides').select('id, channel_id, role_id, user_id, allow, deny').eq('channel_id', channelId),
    ]);

    setRoles((allRoles as ServerRole[]) ?? []);
    const memberIds: string[] = ((vaultRow as any)?.members ?? []) as string[];
    // Fetch profiles for member display names
    if (memberIds.length > 0) {
      const { data: profiles } = await supabase.from('profiles')
        .select('id, display_name, photo_url').in('id', memberIds);
      setMembers(((profiles ?? []) as any[]).map(p => ({
        user_id: p.id,
        display_name: p.display_name,
        photo_url: p.photo_url,
      })));
    } else {
      setMembers([]);
    }
    setOverrides((overrideRows as OverrideRow[]) ?? []);
    setLoading(false);
  }, [channelId, vaultId]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  // ── build target list ───────────────────────────────────────

  const everyoneRole = roles.find(r => r.system_role_id === 'system:everyone' || r.name === '@everyone');
  const permissionRoles = roles.filter(r =>
    r.role_kind !== 'cosmetic' && r.system_role_id !== 'system:everyone' && r.name !== '@everyone',
  );

  // ── shared: find override row for a target ─────────────────

  const getOverrideFor = useCallback((targetKey: string): OverrideRow | undefined => {
    if (targetKey === 'everyone' && everyoneRole) {
      return overrides.find(o => o.role_id === everyoneRole.id);
    }
    if (targetKey.startsWith('role:')) {
      return overrides.find(o => o.role_id === targetKey.slice(5));
    }
    if (targetKey.startsWith('user:')) {
      return overrides.find(o => o.user_id === targetKey.slice(5));
    }
    return undefined;
  }, [overrides, everyoneRole]);

  // ── derive current toggle states for selected target ────────

  useEffect(() => {
    if (!selectedTarget) { setDirtyMask({}); return; }

    const ov = getOverrideFor(selectedTarget);
    const mask: Record<string, ToggleState> = {};
    for (const p of PERMISSIONS) {
      if (ov?.allow_mask?.includes(p.id)) mask[p.id] = 'allow';
      else if (ov?.deny_mask?.includes(p.id)) mask[p.id] = 'deny';
      else mask[p.id] = 'inherit';
    }
    setDirtyMask(mask);
  }, [selectedTarget, getOverrideFor]);

  // ── derive dirty check ──────────────────────────────────────

  const derivedFromOverrides = useCallback((): Record<string, ToggleState> => {
    const ov = getOverrideFor(selectedTarget);
    const mask: Record<string, ToggleState> = {};
    for (const p of PERMISSIONS) {
      if (ov?.allow_mask?.includes(p.id)) mask[p.id] = 'allow';
      else if (ov?.deny_mask?.includes(p.id)) mask[p.id] = 'deny';
      else mask[p.id] = 'inherit';
    }
    return mask;
  }, [selectedTarget, getOverrideFor]);

  const isDirty = (() => {
    const base = derivedFromOverrides();
    const keys = new Set([...Object.keys(base), ...Object.keys(dirtyMask)]);
    for (const k of keys) {
      if ((base[k] ?? 'inherit') !== (dirtyMask[k] ?? 'inherit')) return true;
    }
    return false;
  })();

  // ── toggle a permission ─────────────────────────────────────

  const handleToggle = (permId: string) => {
    setDirtyMask(prev => ({
      ...prev,
      [permId]: nextToggle(prev[permId] ?? 'inherit'),
    }));
  };

  // ── save ────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);

    // Build allow_mask / deny_mask from dirtyMask
    const allow_mask = Object.entries(dirtyMask).filter(([, v]) => v === 'allow').map(([k]) => k);
    const deny_mask = Object.entries(dirtyMask).filter(([, v]) => v === 'deny').map(([k]) => k);

    // Determine role_id / user_id
    let role_id: string | null = null;
    let user_id: string | null = null;
    if (selectedTarget === 'everyone' && everyoneRole) {
      role_id = everyoneRole.id;
    } else if (selectedTarget.startsWith('role:')) {
      role_id = selectedTarget.slice(5);
    } else if (selectedTarget.startsWith('user:')) {
      user_id = selectedTarget.slice(5);
    } else {
      setErrorMsg('No valid target selected.');
      setSaving(false);
      return;
    }

    if (allow_mask.length === 0 && deny_mask.length === 0) {
      // Delete existing override (revert to inherit)
      const match: any = { channel_id: channelId };
      if (role_id) match.role_id = role_id;
      if (user_id) match.user_id = user_id;
      const { error } = await supabase.from('channel_permission_overrides').delete().match(match);
      if (error) setErrorMsg(error.message);
      else {
        setOverrides(prev => prev.filter(o => {
          if (role_id) return o.role_id !== role_id;
          if (user_id) return o.user_id !== user_id;
          return true;
        }));
      }
    } else {
      // Upsert
      const payload = { channel_id: channelId, role_id, user_id, allow_mask, deny_mask };
      // Find existing row for this target
      const existing = overrides.find(o =>
        (role_id ? o.role_id === role_id : o.user_id === user_id));
      if (existing?.id) {
        const { error } = await supabase.from('channel_permission_overrides')
          .update({ allow_mask, deny_mask }).eq('id', existing.id);
        if (error) setErrorMsg(error.message);
        else {
          setOverrides(prev => prev.map(o =>
            o.id === existing.id ? { ...o, allow_mask, deny_mask } : o));
        }
      } else {
        const { data, error } = await supabase.from('channel_permission_overrides')
          .insert(payload).select().single();
        if (error) setErrorMsg(error.message);
        else if (data) {
          setOverrides(prev => [...prev, data as OverrideRow]);
        }
      }
    }

    // Clear error on success (no change if error was set)
    setSaving(false);
  };

  const handleReset = () => {
    setDirtyMask(derivedFromOverrides());
  };

  // ── count overrides for badge ───────────────────────────────

  const countOverridesFor = useCallback((targetKey: string): number => {
    const ov = getOverrideFor(targetKey);
    if (!ov) return 0;
    return (ov.allow_mask?.length ?? 0) + (ov.deny_mask?.length ?? 0);
  }, [getOverrideFor]);

  const currentTargetLabel = (() => {
    if (selectedTarget === 'everyone') return '@everyone';
    if (selectedTarget.startsWith('role:')) {
      const r = roles.find(r => r.id === selectedTarget.slice(5));
      return r ? `@${r.name}` : 'Unknown Role';
    }
    if (selectedTarget.startsWith('user:')) {
      const m = members.find(m => m.user_id === selectedTarget.slice(5));
      return m ? (m.display_name || 'Unknown') : 'Unknown Member';
    }
    return 'Select target…';
  })();

  // ── render ──────────────────────────────────────────────────

  const groupedPerms = groupByCategory(PERMISSIONS);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-white/30 text-xs uppercase tracking-widest">
        Loading permission data…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-black text-white uppercase tracking-widest">Channel Permission Overrides</h3>
        {isDirty && (
          <div className="flex gap-2">
            <button onClick={handleReset}
              className="px-3 py-1.5 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 text-[10px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5">
              <RotateCcw size={11} /> Reset
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 rounded-lg bg-[var(--neon)]/10 border border-[var(--neon)]/30 text-[var(--neon)] hover:bg-[var(--neon)]/20 text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 disabled:opacity-40">
              <Save size={11} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {errorMsg && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-[10px] font-bold">{errorMsg}</div>
      )}

      {/* Target selector */}
      <div className="relative">
        <button
          onClick={() => setShowTargetMenu(!showTargetMenu)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-black/20 border border-white/10 hover:border-white/20 transition-all text-sm text-white"
        >
          <div className="flex items-center gap-2">
            {selectedTarget === 'everyone' ? <Users size={14} className="text-white/50" />
              : selectedTarget.startsWith('role:') ? <Shield size={14} className="text-white/50" />
              : <User size={14} className="text-white/50" />}
            <span className="font-bold">{currentTargetLabel}</span>
          </div>
          <ChevronDown size={14} className={`text-white/40 transition-transform ${showTargetMenu ? 'rotate-180' : ''}`} />
        </button>

        {showTargetMenu && (
          <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-[#0f1225] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-[300px] overflow-y-auto no-scrollbar">
            {/* @everyone */}
            <button
              onClick={() => { setSelectedTarget('everyone'); setShowTargetMenu(false); }}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold transition-all hover:bg-white/5 ${
                selectedTarget === 'everyone' ? 'text-[var(--neon)] bg-[var(--neon)]/5' : 'text-white/70'}`}
            >
              <div className="flex items-center gap-2">
                <Users size={13} /> <span>@everyone</span>
              </div>
              {countOverridesFor('everyone') > 0 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--neon)]/10 text-[var(--neon)] font-black">{countOverridesFor('everyone')}</span>
              )}
            </button>

            {/* Roles */}
            {permissionRoles.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-[9px] font-black text-white/20 uppercase tracking-widest border-t border-white/5">Roles</div>
                {permissionRoles.map(role => (
                  <button key={role.id}
                    onClick={() => { setSelectedTarget(`role:${role.id}`); setShowTargetMenu(false); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold transition-all hover:bg-white/5 ${
                      selectedTarget === `role:${role.id}` ? 'text-[var(--neon)] bg-[var(--neon)]/5' : 'text-white/70'}`}
                  >
                    <div className="flex items-center gap-2">
                      <Shield size={13} style={{ color: role.color || undefined }} />
                      <span>@{role.name}</span>
                    </div>
                    {countOverridesFor(`role:${role.id}`) > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--neon)]/10 text-[var(--neon)] font-black">{countOverridesFor(`role:${role.id}`)}</span>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Members */}
            {members.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-[9px] font-black text-white/20 uppercase tracking-widest border-t border-white/5">Members</div>
                {members.map(member => (
                  <button key={member.user_id}
                    onClick={() => { setSelectedTarget(`user:${member.user_id}`); setShowTargetMenu(false); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold transition-all hover:bg-white/5 ${
                      selectedTarget === `user:${member.user_id}` ? 'text-[var(--neon)] bg-[var(--neon)]/5' : 'text-white/70'}`}
                  >
                    <div className="flex items-center gap-2">
                      <User size={13} />
                      <span>{member.display_name || member.user_id.slice(0, 8)}</span>
                    </div>
                    {countOverridesFor(`user:${member.user_id}`) > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--neon)]/10 text-[var(--neon)] font-black">{countOverridesFor(`user:${member.user_id}`)}</span>
                    )}
                  </button>
                ))}
              </>
            )}

            {permissionRoles.length === 0 && members.length === 0 && (
              <div className="px-4 py-4 text-[10px] text-white/30 text-center">No roles or members to show.</div>
            )}
          </div>
        )}
      </div>

      {/* Close target menu on click outside */}
      {showTargetMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowTargetMenu(false)} />
      )}

      {/* Permission grid, grouped by category */}
      <div className="space-y-4">
        {Array.from(groupedPerms.entries())
          .sort(([a], [b]) => {
            const ai = CATEGORY_ORDER.indexOf(a as any);
            const bi = CATEGORY_ORDER.indexOf(b as any);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
          })
          .map(([cat, perms]) => {
          if (perms.length === 0) return null;
          return (
            <div key={cat}>
              <h4 className="text-[9px] font-black text-white/20 uppercase tracking-widest mb-2 px-1">{CATEGORY_LABEL[cat] ?? cat}</h4>
              <div className="rounded-xl bg-black/10 border border-white/5 overflow-hidden">
                {perms.map((perm, i) => {
                  const state = dirtyMask[perm.id] ?? 'inherit';
                  return (
                    <div
                      key={perm.id}
                      className={`flex items-center justify-between px-4 py-3 transition-colors hover:bg-white/[0.02] ${
                        i < perms.length - 1 ? 'border-b border-white/5' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">{perm.name}</span>
                          {perm.danger === 'dangerous' && (
                            <span className="text-[8px] px-1 py-0.5 rounded bg-red-500/10 text-red-400 font-black uppercase">Danger</span>
                          )}
                        </div>
                        <p className="text-[10px] text-white/30 mt-0.5 line-clamp-1">{perm.description}</p>
                      </div>

                      <button
                        onClick={() => handleToggle(perm.id)}
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-widest transition-all ${TOGGLE_CLASS[state]} hover:brightness-125`}
                        title={TOGGLE_LABEL[state]}
                      >
                        {TOGGLE_ICON[state]}
                        <span className="hidden sm:inline">{TOGGLE_LABEL[state]}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {PERMISSIONS.length === 0 && (
        <div className="text-center py-8 text-white/30 text-xs">No permissions defined.</div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-[9px] font-bold text-white/20 uppercase tracking-widest px-1">
        <span className="flex items-center gap-1"><Check size={10} className="text-emerald-400" /> Allow</span>
        <span className="flex items-center gap-1"><XIcon size={10} className="text-red-400" /> Deny</span>
        <span className="flex items-center gap-1"><Minus size={10} /> Inherit</span>
      </div>
    </div>
  );
}
